import { Link } from "react-router-dom";
import { ThumbsUp, ThumbsDown, Minus, ArrowRight, AlertTriangle, MessageSquare } from "lucide-react";
import { Card, CardHeader, CardFooter } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { useShell } from "@/components/Shell";
import { relative, absolute } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * Recent conversations preview. Shows up to 4 latest sessions with the
 * first Q + first A snippet, plus a feedback indicator. Click → opens the
 * conversation in the Conversations route (TODO: side panel reuse).
 */
export function RecentConversationsCard() {
  const { home, loading } = useShell();

  if (loading && !home) return <RecentConversationsSkeleton />;
  if (!home) return null;

  const items = home.recent_conversations;

  return (
    <Card>
      <CardHeader title="Recent conversations" />

      {items.length === 0 ? (
        <div className="px-5 pb-5 pt-2">
          <EmptyState
            icon={MessageSquare}
            title="No conversations yet"
            description="Add the widget to your site to start receiving visitor questions."
          />
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((c) => (
            <li key={c.id} className="px-5 py-3.5">
              <Link to={`/conversations?id=${c.id}`} className="group block">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <FeedbackIcon kind={c.assistant_feedback} grounded={c.assistant_grounded} />
                      <p
                        className="truncate text-sm font-medium text-text-strong"
                        title={c.first_user_message ?? ""}
                      >
                        {c.first_user_message ?? "(no question recorded)"}
                      </p>
                    </div>
                    {c.first_assistant_message && (
                      <p className="mt-1 line-clamp-1 text-xs text-text-soft">
                        {c.first_assistant_message}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-text-faint">
                      <span title={absolute(c.last_message_at)} className="cursor-default">
                        {relative(c.last_message_at)}
                      </span>
                      <span className="mx-1.5">·</span>
                      <span className="tabular-nums">{c.message_count}</span>{" "}
                      {c.message_count === 1 ? "message" : "messages"}
                    </p>
                  </div>
                  <ArrowRight
                    size={14}
                    strokeWidth={1.75}
                    className="mt-1 shrink-0 text-text-faint group-hover:text-coral"
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <CardFooter>
          <Link
            to="/conversations"
            className="inline-flex items-center gap-1 text-xs text-text-soft hover:text-text-strong"
          >
            <MessageSquare size={12} strokeWidth={1.75} />
            Open conversations
          </Link>
        </CardFooter>
      )}
    </Card>
  );
}

function FeedbackIcon({
  kind,
  grounded,
}: {
  kind: "positive" | "negative" | null;
  grounded: boolean | null;
}) {
  if (kind === "positive") {
    return <ThumbsUp size={14} strokeWidth={1.75} className="shrink-0 text-success" aria-label="Positive feedback" />;
  }
  if (kind === "negative") {
    return <ThumbsDown size={14} strokeWidth={1.75} className="shrink-0 text-danger" aria-label="Negative feedback" />;
  }
  // No explicit feedback, but flag if the bot answered without grounding —
  // that's worth eyeballing.
  if (grounded === false) {
    return <AlertTriangle size={14} strokeWidth={1.75} className="shrink-0 text-warning" aria-label="No retrieval" />;
  }
  return <Minus size={14} strokeWidth={1.75} className={cn("shrink-0 text-text-faint")} aria-label="No feedback" />;
}

function RecentConversationsSkeleton() {
  return (
    <Card>
      <CardHeader title="Recent conversations" />
      <ul className="divide-y divide-line">
        {[0, 1, 2, 3].map((i) => (
          <li key={i} className="px-5 py-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="mt-2 h-3 w-2/3" />
            <Skeleton className="mt-2 h-3 w-1/3" />
          </li>
        ))}
      </ul>
    </Card>
  );
}
